import React, { useEffect, useState } from "react";
import { Download, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

export default function Downloads() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [filterLang, setFilterLang] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const opts = {};
    if (filterLang) opts.language = filterLang;
    api.get("/downloads", { params: opts })
      .then(r => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [filterLang]);

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 md:py-16">
      <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">{t.sections.downloads}</h1>
      <p className="mt-2 text-muted-foreground">{t.sections.downloadsSub}</p>

      <div className="mt-6 flex flex-wrap gap-2">
        <button
          onClick={() => setFilterLang("")}
          data-testid="dl-lang-all"
          className={`px-4 py-2 rounded-full text-sm font-medium transition ${!filterLang ? "bg-foreground text-white" : "bg-muted"}`}
        >
          {t.common.viewAll}
        </button>
        {["es", "en", "fr"].map(l => (
          <button
            key={l}
            onClick={() => setFilterLang(l)}
            data-testid={`dl-lang-${l}`}
            className={`px-4 py-2 rounded-full text-sm font-medium uppercase transition ${
              filterLang === l ? "bg-[hsl(var(--primary-green))] text-white" : "bg-muted"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {loading ? (
          <p className="text-muted-foreground">{t.common.loading}</p>
        ) : items.length === 0 ? (
          <div className="py-20 text-center" data-testid="downloads-empty">
            <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">{t.common.noResults}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="downloads-grid">
            {items.map(d => (
              <a
                key={d.id}
                href={`${api.defaults.baseURL}/files/${d.id}/download`}
                target="_blank"
                rel="noreferrer"
                data-testid={`download-card-${d.id}`}
                className="group flex flex-col p-5 bg-card border border-border rounded-2xl hover:-translate-y-1 hover:shadow-xl transition-all"
              >
                <div className="w-12 h-12 rounded-2xl bg-[hsl(var(--primary-red))]/10 text-[hsl(var(--primary-red))] flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6" />
                </div>
                <h3 className="font-display font-semibold text-lg leading-tight">{d.title}</h3>
                {d.description && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{d.description}</p>}
                <div className="mt-auto pt-4 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground uppercase font-semibold">{d.extension} · {Math.round(d.size / 1024)} KB · {d.language?.toUpperCase()}</span>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--primary-green))] group-hover:gap-2 transition-all">
                    <Download className="w-4 h-4" />
                  </span>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
